// src/layout/Layout.jsx
import { Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import Menu from "./Menu/Menu";
import CookieConsent from "../components/shared/CookieConsent";
import ChatWidget from "../components/shared/ChatWidget";
import { Box } from "@chakra-ui/react";
import { I18nextProvider } from "react-i18next";
import i18n from "../config/i18n";
import { useAuth } from "../context/AuthContext";

// Función para calcular distancia entre dos puntos (fórmula Haversine)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Radio de la Tierra en metros
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distancia en metros
};

const Layout = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  // Detectar parámetro lang en la URL y cambiar idioma
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    
    if (langParam) {
      // Lista de idiomas válidos
      const validLanguages = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca', 'zh'];
      
      if (validLanguages.includes(langParam)) {
        console.log(`[LANG] Cambiando idioma a: ${langParam}`);
        i18n.changeLanguage(langParam);
        localStorage.setItem('appLanguage', langParam);
      } else {
        console.warn(`[LANG] Idioma no válido: ${langParam}. Idiomas disponibles: ${validLanguages.join(', ')}`);
      }
    }
  }, [location.search]);

  // Función helper para logs con timestamp
  const logWithTime = (message) => {
    const time = new Date().toLocaleTimeString('es-ES', { hour12: false });
    console.log(`[${time}] ${message}`);
  };

  const logWarnWithTime = (message) => {
    const time = new Date().toLocaleTimeString('es-ES', { hour12: false });
    console.warn(`[${time}] ${message}`);
  };

  const logErrorWithTime = (message) => {
    const time = new Date().toLocaleTimeString('es-ES', { hour12: false });
    console.error(`[${time}] ${message}`);
  };

  useEffect(() => {
    // Solo activar tracking para usuarios autenticados (no administradores)
    if (!isAuthenticated || !user) {
      return;
    }

    // Excluir administradores del tracking de ubicación
    if (user.user_type === 'administrador') {
      logWithTime('[TRACKING] Tracking de ubicación deshabilitado para administradores');
      return;
    }

    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('auth-token');
    
    if (!token) {
      logWarnWithTime('[TRACKING] No hay token disponible para enviar ubicación');
      return;
    }

    // Detectar tipo de dispositivo
    const isMobile = /Mobile|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
    
    logWithTime(`[TRACKING] Iniciando tracking para ${user.user_type} (${isMobile ? 'Móvil' : 'PC'})`);

    // Función para enviar ubicación al servidor
    const sendLocation = async (position) => {
      try {
        // Obtener última posición guardada
        const lastPosStr = localStorage.getItem('lastPosition');
        const lastPos = lastPosStr ? JSON.parse(lastPosStr) : null;

        // Obtener timestamp de la última actualización exitosa
        const lastUpdateStr = localStorage.getItem('lastUpdateTime');
        const lastUpdateTime = lastUpdateStr ? parseInt(lastUpdateStr) : 0;
        const timeSinceLastUpdate = Date.now() - lastUpdateTime;
        const twentyMinutes = 20 * 60 * 1000; // 20 minutos en milisegundos

        // Calcular distancia si existe posición anterior
        let shouldSend = true;
        if (lastPos) {
          const distance = calculateDistance(
            lastPos.latitude,
            lastPos.longitude,
            position.coords.latitude,
            position.coords.longitude
          );

          // Solo enviar si cambió más de 50 metros O han pasado más de 20 minutos
          if (distance < 50 && timeSinceLastUpdate < twentyMinutes) {
            logWithTime(`[TRACKING] Cambio insignificante (${distance.toFixed(0)}m) y última actualización reciente, no se envía`);
            shouldSend = false;
          } else if (distance < 50 && timeSinceLastUpdate >= twentyMinutes) {
            logWithTime(`[TRACKING] Sin cambio significativo pero >20 min desde última actualización, enviando heartbeat`);
          } else {
            logWithTime(`[TRACKING] Cambio significativo (${distance.toFixed(0)}m), enviando actualización`);
          }
        }

        if (!shouldSend) {
          return;
        }

        const payload = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };

        const response = await fetch(`${baseURL}/api/ubicaciones/actualizar-posicion`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();
          logWithTime('[TRACKING] Ubicación actualizada: ' + data.message);
          
          // Guardar última posición y timestamp de actualización exitosa
          localStorage.setItem('lastPosition', JSON.stringify({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
          localStorage.setItem('lastUpdateTime', Date.now().toString());
        } else {
          let detail = '';
          try {
            const body = await response.json();
            detail = body?.detail ? ` - ${body.detail}` : '';
          } catch {
            // noop
          }

          if (response.status === 401 || response.status === 403) {
            logWarnWithTime('[TRACKING] Tracking no autorizado: ' + response.status + detail);
          } else {
            logErrorWithTime('[TRACKING] Error al actualizar ubicación: ' + response.status + detail);
          }
        }
      } catch (error) {
        logErrorWithTime('[TRACKING] Error al enviar ubicación: ' + error);
      }
    };

    const handleError = (error) => {
      logWarnWithTime('[TRACKING] Error de geolocalización: ' + error.message);
    };

    let intervalId;
    let watchId;

    if (navigator.geolocation) {
      if (isMobile) {
        // MÓVIL: watchPosition con debounce de 30s
        let lastSent = 0;
        const debounceMs = 30000; // 30 segundos

        watchId = navigator.geolocation.watchPosition(
          (position) => {
            const now = Date.now();
            if (now - lastSent >= debounceMs) {
              sendLocation(position);
              lastSent = now;
            }
          },
          handleError,
          {
            enableHighAccuracy: true,
            maximumAge: 30000,
            timeout: 10000
          }
        );

        logWithTime('[TRACKING] watchPosition activado para móvil (debounce 30s)');
      } else {
        // PC: getCurrentPosition cada 5 minutos
        const sendCurrentPosition = () => {
          navigator.geolocation.getCurrentPosition(sendLocation, handleError, {
            enableHighAccuracy: false,
            maximumAge: 300000, // 5 minutos
            timeout: 10000
          });
        };

        // Enviar posición inicial
        sendCurrentPosition();

        // Configurar intervalo de 5 minutos
        intervalId = setInterval(sendCurrentPosition, 300000); // 5 minutos

        logWithTime('[TRACKING] Intervalo de 5 minutos activado para PC');
      }
    } else {
      logWarnWithTime('[TRACKING] Geolocalización no soportada en este navegador');
    }

    // Cleanup al desmontar
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        logWithTime('[TRACKING] watchPosition detenido');
      }
      if (intervalId) {
        clearInterval(intervalId);
        logWithTime('[TRACKING] Intervalo detenido');
      }
    };
  }, [isAuthenticated, user]);

  return (
    <I18nextProvider i18n={i18n}>
      <Box
        bg="black"
        color="white"
        minH="100vh"
        display="flex"
        flexDirection="column"
      >
        <Header />
        <Menu />
        <Box as="main" flex="1 0 auto" w="100%">
          <Outlet />
        </Box>
        <ChatWidget />
        <Footer />
        <CookieConsent />
      </Box>
    </I18nextProvider>
  );
};

export default Layout;
